import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { GiGrass, GiTreeBranch, GiFlowerPot, GiGardeningShears } from 'react-icons/gi';
import { MdYard, MdDeleteSweep } from 'react-icons/md';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ContactCTA } from '@/components/features/ContactCTA';

const gardeningServices = [
  {
    icon: <GiGrass className="h-8 w-8" />,
    title: 'Lawn Mowing & Edging',
    description: 'Regular lawn maintenance to keep your yard looking pristine.',
  },
  {
    icon: <GiGardeningShears className="h-8 w-8" />,
    title: 'Hedge Trimming',
    description: 'Professional shaping and trimming of hedges and shrubs.',
  },
  {
    icon: <GiFlowerPot className="h-8 w-8" />,
    title: 'Garden Bed Maintenance',
    description: 'Weeding, planting, and ongoing care for garden beds.',
  },
  {
    icon: <MdYard className="h-8 w-8" />,
    title: 'Mulching & Fertilising',
    description: 'Soil enrichment and mulching for healthy plant growth.',
  },
  {
    icon: <MdDeleteSweep className="h-8 w-8" />,
    title: 'Green Waste Removal',
    description: 'Clean-up and removal of garden waste and debris.',
  },
  {
    icon: <GiTreeBranch className="h-8 w-8" />,
    title: 'Seasonal Planting',
    description: 'Expert plant selection and installation for each season.',
  },
];

/**
 * Gardening services page
 */
export function Gardening() {
  return (
    <>
      {/* Page Header */}
      <section className="section-padding bg-gradient-to-b from-primary/5 to-transparent">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="category-label">Outdoor Services</span>
            <h1 className="text-4xl font-bold md:text-5xl">
              Gardening Services
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Professional lawn care and garden maintenance services in Tasmania.
              From regular mowing to complete garden transformations, I'll help your
              outdoor spaces thrive.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild size="lg" className="gap-2">
                <Link to="/contact">
                  Request Quote
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/services">All Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding" aria-labelledby="gardening-services">
        <div className="container-custom">
          <div className="mb-12">
            <span className="category-label">What's Included</span>
            <h2 id="gardening-services" className="text-3xl font-bold">
              Our Gardening Services
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {gardeningServices.map((service, index) => (
              <Card key={index} className="card-hover">
                <CardContent className="pt-6">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-muted text-primary">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-semibold">{service.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us & Pricing */}
      <section className="section-padding bg-card" aria-labelledby="why-choose">
        <div className="container-custom">
          {/* Feature Card - GitBook style */}
          <Card className="overflow-hidden rounded-2xl border-0 bg-muted/50">
            <div className="grid lg:grid-cols-5">
              {/* Left Content - Why Choose Us */}
              <div className="p-8 lg:col-span-3 lg:p-12">
                <span className="category-label">Why Choose Us</span>
                <h2 id="why-choose" className="mt-2 text-3xl font-bold">
                  Quality You Can Trust
                </h2>
                <p className="mt-4 text-muted-foreground">
                  With attention to detail and a passion for outdoor spaces,
                  I deliver gardening services that exceed expectations.
                </p>
                <ul className="mt-8 space-y-4">
                  {[
                    'Reliable and punctual service',
                    'All equipment provided',
                    'Eco-friendly practices',
                    'Tailored to your needs',
                    'Competitive pricing',
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Check className="h-4 w-4" aria-hidden="true" />
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right Content - Pricing */}
              <div className="flex items-center justify-center border-t border-border/50 bg-gradient-to-br from-primary/5 to-primary/10 p-8 lg:col-span-2 lg:border-l lg:border-t-0 lg:p-12">
                <div className="text-center">
                  <span className="category-label">Pricing</span>
                  <h3 className="mt-2 text-2xl font-bold">Price Upon Request</h3>
                  <p className="mx-auto mt-4 max-w-xs text-sm text-muted-foreground">
                    Every garden is unique. Contact me for a free assessment and
                    personalised quote tailored to your specific needs.
                  </p>
                  <Button asChild className="mt-6 gap-2">
                    <Link to="/contact">
                      Get Your Quote
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Contact CTA */}
      <ContactCTA />
    </>
  );
}
